/**
 * Gemini 3.5 Transcribe (live) — mic PCM → Live API streaming STT.
 *
 * Additive overlay only: callers keep their default Web Speech path when the
 * per-app flag is off. Failures throw / call onError so the caller can fall
 * closed to the current STT engine.
 */

import { GoogleGenAI, Modality } from '@google/genai';
import { createDeepgramMicCapture } from 'components/voice/deepgramMicCapture';
import {
  encodeLittleEndianPcm16Base64,
  fetchGeminiLiveEphemeralToken,
} from './geminiLiveSession';
import { GEMINI_35_TRANSCRIBE_LIVE_MODEL } from './gemini35TranscribeConstants';

const INPUT_PCM_RATE = 16000;

/**
 * @param {Object} [options]
 * @param {'smart'|'verbatim'} [options.mode='smart']
 * @param {string[]} [options.languageCodes] BCP-47 hints; omit/empty = auto-detect
 * @param {(text: string) => void} [options.onInterim]
 * @param {(text: string) => void} [options.onFinal] Emitted for each finalized segment
 * @param {(err: { code?: string, message: string }) => void} [options.onError]
 * @returns {{
 *   start: () => Promise<void>,
 *   stop: () => Promise<string>,
 *   isListening: () => boolean,
 * }}
 */
export function createGemini35TranscribeCapture(options = {}) {
  const mode = options.mode === 'verbatim' ? 'verbatim' : 'smart';
  const languageCodes = Array.isArray(options.languageCodes)
    ? options.languageCodes.filter((c) => typeof c === 'string' && c.trim())
    : [];
  const onInterim = typeof options.onInterim === 'function' ? options.onInterim : null;
  const onFinal = typeof options.onFinal === 'function' ? options.onFinal : null;
  const onError = typeof options.onError === 'function' ? options.onError : null;

  let session = null;
  let mic = null;
  let listening = false;
  let startPromise = null;
  /** @type {string[]} */
  const finalParts = [];
  let lastInterim = '';

  function emitError(code, message) {
    if (onError) onError({ code, message });
  }

  function handleServerMessage(msg) {
    const sc = msg?.serverContent;
    if (!sc) return;

    const interim =
      sc.interimInputTranscription?.text ||
      sc.inputTranscription?.text;
    // Prefer dedicated interim field when present; otherwise treat inputTranscription
    // as final (Live Transcribe emits both shapes depending on mode/endpoint).
    if (sc.interimInputTranscription?.text) {
      lastInterim = String(sc.interimInputTranscription.text || '').trim();
      if (lastInterim && onInterim) onInterim(lastInterim);
    }
    if (sc.inputTranscription?.text && !sc.interimInputTranscription) {
      const text = String(sc.inputTranscription.text || '').trim();
      if (text) {
        finalParts.push(text);
        lastInterim = '';
        if (onFinal) onFinal(text);
      }
    } else if (
      sc.inputTranscription?.text &&
      sc.turnComplete
    ) {
      const text = String(sc.inputTranscription.text || '').trim();
      if (text) {
        finalParts.push(text);
        lastInterim = '';
        if (onFinal) onFinal(text);
      }
    }
  }

  async function start() {
    if (listening) return;
    if (startPromise) return startPromise;

    startPromise = (async () => {
      const { ephemeralToken, model } = await fetchGeminiLiveEphemeralToken({
        model: GEMINI_35_TRANSCRIBE_LIVE_MODEL,
        signInAnonymouslyIfNeeded: false,
      });

      const ai = new GoogleGenAI({
        apiKey: ephemeralToken,
        httpOptions: { apiVersion: 'v1alpha' },
      });

      const inputAudioTranscription = { mode };
      if (languageCodes.length) {
        inputAudioTranscription.languageCodes = languageCodes;
      }

      // Omit generationConfig when possible — TEXT responseModalities can suppress
      // final inputTranscription on some endpoints (empirically). Prefer modality TEXT
      // only as required by the Live Transcribe skill docs.
      session = await ai.live.connect({
        model: model || GEMINI_35_TRANSCRIBE_LIVE_MODEL,
        config: {
          responseModalities: [Modality.TEXT],
          inputAudioTranscription,
        },
        callbacks: {
          onopen: () => {},
          onmessage: (msg) => {
            try {
              handleServerMessage(msg);
            } catch (err) {
              console.warn('[gemini35Transcribe] onmessage', err?.message || err);
            }
          },
          onerror: (err) => {
            emitError('session', err?.message || 'Gemini 3.5 Transcribe session error');
          },
          onclose: () => {
            listening = false;
          },
        },
      });

      mic = createDeepgramMicCapture({
        onAudioData: (arrayBuffer) => {
          if (!session || !listening) return;
          try {
            const int16 = new Int16Array(arrayBuffer);
            const audio = {
              mimeType: `audio/pcm;rate=${INPUT_PCM_RATE}`,
              data: encodeLittleEndianPcm16Base64(int16),
            };
            session.sendRealtimeInput({ audio });
          } catch (err) {
            const msg = err?.message || String(err);
            if (msg.includes('CLOSING') || msg.includes('CLOSED')) return;
            console.warn('[gemini35Transcribe] sendRealtimeInput', msg);
          }
        },
      });

      await mic.start();
      listening = true;
    })();

    try {
      await startPromise;
    } catch (err) {
      await stopInternal(false);
      throw err;
    } finally {
      startPromise = null;
    }
  }

  async function stopInternal(signalEnd) {
    listening = false;
    if (mic) {
      try {
        mic.stop();
      } catch {
        /* ignore */
      }
      mic = null;
    }
    if (session) {
      try {
        if (signalEnd && typeof session.sendRealtimeInput === 'function') {
          try {
            session.sendRealtimeInput({ audioStreamEnd: true });
          } catch {
            /* ignore */
          }
        }
        session.close?.();
      } catch {
        /* ignore */
      }
      session = null;
    }
  }

  async function stop() {
    // Promote leftover interim to final if the server never finalized.
    if (lastInterim) {
      finalParts.push(lastInterim);
      if (onFinal) onFinal(lastInterim);
      lastInterim = '';
    }
    await stopInternal(true);
    return finalParts.join(' ').replace(/\s+/g, ' ').trim();
  }

  return {
    start,
    stop,
    isListening: () => listening,
  };
}
